import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { TableCellNode, TableNode } from '@lexical/table';
import {
  $deleteTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $insertTableColumnAtSelection,
  $insertTableRowAtSelection,
  $mergeCells,
  $setTableColumnIsHeader,
  $setTableRowIsHeader,
  $unmergeCell,
} from '@lexical/table';
import type { LucideIcon } from 'lucide-react';
import {
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  AlignVerticalJustifyStart,
  ArrowDownToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUpToLine,
  Ban,
  PanelLeft,
  PanelTop,
  Rows3,
  TableCellsMerge,
  TableCellsSplit,
  Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ToolbarButton } from '../../ui/ToolbarButton';
import { ToolbarMenuItem, ToolbarMenuLabel } from '../../ui/ToolbarMenu';
import type { ActiveCell } from './tableSelection';
import { $getSelectedCells, $getSelectedTable } from './tableSelection';

/** A light subset of Froala's default `colorsBackground` palette. */
const CELL_COLOURS = ['#FBE7A1', '#FAD7A0', '#F5B7B1', '#D7BDE2', '#AED6F1', '#A3E4D7', '#ABEBC6', '#EAEDED', '#D5D8DC', '#ABB2B9'];

const VERTICAL_ALIGNMENTS = [
  { value: 'top', icon: AlignVerticalJustifyStart, labelKey: 'editor.table.align-top' },
  { value: 'middle', icon: AlignVerticalJustifyCenter, labelKey: 'editor.table.align-middle' },
  { value: 'bottom', icon: AlignVerticalJustifyEnd, labelKey: 'editor.table.align-bottom' },
] as const satisfies readonly { value: string; icon: LucideIcon; labelKey: string }[];

type SectionProps = { active: ActiveCell; close: () => void };

/**
 * Editor updates on the current selection.
 * - `run` is for structural actions (insert/delete/merge), which close the menu.
 * - `updateCells` / `updateTable` are for toggles and styling, which keep it open.
 */
const useTableActions = (close: () => void) => {
  const [editor] = useLexicalComposerContext();

  return {
    run: (action: () => void) => {
      editor.update(action);
      close();
    },
    updateCells: (mutate: (cell: TableCellNode) => void) => editor.update(() => $getSelectedCells()?.cells.forEach(mutate)),
    updateTable: (mutate: (table: TableNode) => void) =>
      editor.update(() => {
        const table = $getSelectedTable();
        if (table) mutate(table);
      }),
  };
};

const RowSection = ({ active, close }: SectionProps) => {
  const { t } = useTranslation('common');
  const { run, updateTable } = useTableActions(close);

  return (
    <section>
      <ToolbarMenuLabel>{t('editor.table.row')}</ToolbarMenuLabel>
      <ToolbarMenuItem icon={ArrowUpToLine} label={t('editor.table.insert-row-above')} onClick={() => run(() => $insertTableRowAtSelection(false))} />
      <ToolbarMenuItem
        icon={ArrowDownToLine}
        label={t('editor.table.insert-row-below')}
        onClick={() => run(() => $insertTableRowAtSelection(true))}
      />
      <ToolbarMenuItem
        icon={PanelTop}
        label={t('editor.table.header-row')}
        checked={active.rowIsHeader}
        onClick={() => updateTable((table) => $setTableRowIsHeader(table, active.rowIndex, !active.rowIsHeader))}
      />
      <ToolbarMenuItem icon={Trash2} label={t('editor.table.delete-row')} onClick={() => run($deleteTableRowAtSelection)} />
    </section>
  );
};

const ColumnSection = ({ active, close }: SectionProps) => {
  const { t } = useTranslation('common');
  const { run, updateTable } = useTableActions(close);

  return (
    <section className="border-greyscale-15 border-l">
      <ToolbarMenuLabel>{t('editor.table.column')}</ToolbarMenuLabel>
      <ToolbarMenuItem
        icon={ArrowLeftToLine}
        label={t('editor.table.insert-column-left')}
        onClick={() => run(() => $insertTableColumnAtSelection(false))}
      />
      <ToolbarMenuItem
        icon={ArrowRightToLine}
        label={t('editor.table.insert-column-right')}
        onClick={() => run(() => $insertTableColumnAtSelection(true))}
      />
      <ToolbarMenuItem
        icon={PanelLeft}
        label={t('editor.table.header-column')}
        checked={active.columnIsHeader}
        onClick={() => updateTable((table) => $setTableColumnIsHeader(table, active.columnIndex, !active.columnIsHeader))}
      />
      <ToolbarMenuItem icon={Trash2} label={t('editor.table.delete-column')} onClick={() => run($deleteTableColumnAtSelection)} />
    </section>
  );
};

const CellSection = ({ active, close }: SectionProps) => {
  const { t } = useTranslation('common');
  const { run, updateCells } = useTableActions(close);

  return (
    <section className="border-greyscale-15 border-t">
      <ToolbarMenuLabel>{t('editor.table.cell')}</ToolbarMenuLabel>
      <ToolbarMenuItem
        icon={TableCellsMerge}
        label={t('editor.table.merge-cells')}
        disabled={!active.canMerge}
        onClick={() =>
          run(() => {
            const selected = $getSelectedCells();
            if (selected) $mergeCells(selected.cells);
          })
        }
      />
      <ToolbarMenuItem icon={TableCellsSplit} label={t('editor.table.split-cell')} disabled={!active.canUnmerge} onClick={() => run($unmergeCell)} />
      <div className="flex items-center gap-0.5 px-2 py-1" role="group" aria-label={t('editor.table.vertical-align')}>
        {VERTICAL_ALIGNMENTS.map(({ value, icon, labelKey }) => (
          <ToolbarButton
            key={value}
            icon={icon}
            label={t(labelKey)}
            active={active.verticalAlign === value}
            onClick={() => updateCells((cell) => cell.setVerticalAlign(value))}
          />
        ))}
      </div>
      <div className="grid grid-cols-6 gap-1 px-3 pt-1 pb-2" role="group" aria-label={t('editor.table.background')}>
        {CELL_COLOURS.map((colour) => (
          <button
            key={colour}
            type="button"
            title={colour}
            aria-label={colour}
            className="border-greyscale-15 size-6 cursor-pointer rounded border"
            style={{ backgroundColor: colour }}
            onClick={() => updateCells((cell) => cell.setBackgroundColor(colour))}
          />
        ))}
        <button
          type="button"
          title={t('editor.table.no-background')}
          aria-label={t('editor.table.no-background')}
          className="border-greyscale-15 text-text-2 flex size-6 cursor-pointer items-center justify-center rounded border bg-white"
          onClick={() => updateCells((cell) => cell.setBackgroundColor(null))}
        >
          <Ban className="size-3.5" aria-hidden />
        </button>
      </div>
    </section>
  );
};

const TableSection = ({ active, close }: SectionProps) => {
  const { t } = useTranslation('common');
  const { run, updateTable } = useTableActions(close);

  return (
    <section className="border-greyscale-15 border-t border-l">
      <ToolbarMenuLabel>{t('editor.table.table')}</ToolbarMenuLabel>
      <ToolbarMenuItem
        icon={Rows3}
        label={t('editor.table.striped-rows')}
        checked={active.striped}
        onClick={() => updateTable((table) => table.setRowStriping(!active.striped))}
      />
      <ToolbarMenuItem
        icon={Trash2}
        label={t('editor.table.delete-table')}
        onClick={() =>
          run(() => {
            $getSelectedTable()?.remove();
          })
        }
      />
    </section>
  );
};

/** The cell menu's 2×2 grid: Row | Column over Cell | Table. */
export const TableActionMenuContent = (props: SectionProps) => (
  <div className="grid w-max grid-cols-2">
    <RowSection {...props} />
    <ColumnSection {...props} />
    <CellSection {...props} />
    <TableSection {...props} />
  </div>
);
